import { useEffect, useRef, useState } from "react";
import { CaretDown, Printer, Receipt, FileText } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const openAndPrint = async (path) => {
  const { data } = await api.get(path, { responseType: "blob" });
  const w = window.open(URL.createObjectURL(data));
  if (w) setTimeout(() => w.print(), 600);
};

export default function InvoicePrintMenu({ invoiceId, saleId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const printThermal = async () => {
    setOpen(false);
    try {
      const [{ data: status }, { data: prefs }] = await Promise.all([
        api.get("/print/status").catch(() => ({ data: {} })),
        api.get("/users/me/print-prefs").catch(() => ({ data: {} })),
      ]);
      if (status?.configured && prefs?.receipt_printer_id) {
        try {
          await api.post(`/print/receipt/${saleId}`, { printer_id: prefs.receipt_printer_id, copies: 1 });
          toast.success("Receipt sent to thermal printer");
          return;
        } catch (e) {
          toast.error(`Thermal printer unavailable (${formatApiError(e)}) — opening receipt to print`);
        }
      }
      await openAndPrint(`/sales/${saleId}/receipt/pdf`);
    } catch (e) { toast.error(formatApiError(e) || "Thermal print failed"); }
  };

  const printA4 = async () => {
    setOpen(false);
    try { await openAndPrint(`/online-invoices/${invoiceId}/pdf`); } catch { toast.error("Print failed"); }
  };

  const item = "w-full px-3 py-2.5 text-left text-[12px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} className="h-10 px-3.5 rounded-xl border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,border-color,transform]" data-testid="inv-print">
        <Printer size={15} weight="bold" /> Print <CaretDown size={11} weight="bold" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 w-52 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden z-10" data-testid="inv-print-menu">
          <button onClick={printThermal} disabled={!saleId} className={item} data-testid="inv-print-thermal" title={saleId ? "" : "No in-store sale linked to this invoice yet"}>
            <Receipt size={14} weight="bold" /> Thermal receipt
          </button>
          <button onClick={printA4} className={`${item} border-t border-slate-100`} data-testid="inv-print-a4">
            <FileText size={14} weight="bold" /> A4 invoice
          </button>
        </div>
      )}
    </div>
  );
}
